import { useMemo } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { motion } from 'framer-motion';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import {
    useGlobalInfo,
    CLEAR_GROUP_INFO,
    UPDATE_ACTIVE_SOCIAL_PROFILE
} from '@/hooks/context/useGlobalInfo';

// Components
import { AvatarMenu } from '@/components/composite/SocialProfiles/Components/AvatarMenu';
import { RightMenuPopover } from '@/components/composite/SocialProfiles/SocialProfileGroupMenu/Components/RightMenuPopover';

// Hooks
import { SP_TRANSITION_DURATION_MS } from '@/hooks/socialprofiles/useSocialProfileTransition';

//utils
import { updateActiveItem } from '@/utils/menu/updateActiveItem';
import { getMenuItemsOrder } from '@/utils/menu/getMenuItemsOrder';

//libs
import {
    nbItemsDisplayed,
    nbItemsDisplayedNoActiveItem
} from '@/lib/menu/RightMenu/itemsDisplayed';
import type { SocialProfileType } from '@/types/socialProfileType';

type SocialProfilesMenuContentProps = ComponentPropsWithoutRef<'div'> &
    Pick<
        SocialProfilesMenuProps,
        'initialSocialProfilePosition' | 'setInitialSocialProfilePosition'
    > & {
        activeItem?: SocialProfileType;
        setActiveItem: (item: SocialProfileType) => void;
        itemsList: SocialProfileType[];
        variant: 'social-profile' | 'group';
    };
export function SocialProfilesMenuContent({
    activeItem,
    setActiveItem,
    itemsList,
    initialSocialProfilePosition,
    setInitialSocialProfilePosition,
    variant,
    ...restProps
}: SocialProfilesMenuContentProps) {
    const { dispatch, globalInfo } = useGlobalInfo();
    const nbSocialProfiles = itemsList?.length;
    const nbMoreSocialProfiles = nbSocialProfiles - nbItemsDisplayedNoActiveItem;

    const orderedItems = useMemo(() => {
        return getMenuItemsOrder<SocialProfileType>({
            storageKey:
                globalInfo.group.activeGroup && globalInfo.menuGroupActive
                    ? `ico-sp-group-order-${globalInfo.group.activeGroup.id}`
                    : 'ico-sp-order',
            menuItems: itemsList,
            activeItemId: activeItem?.id
        });
    }, [itemsList, activeItem, globalInfo.group.activeGroup, globalInfo.menuGroupActive]);

    const updateActive = (item: SocialProfileType, initialPosition: number) => {
        updateActiveItem<SocialProfileType>({
            storageKey: globalInfo.group.activeGroup
                ? `ico-sp-group-order-${globalInfo.group.activeGroup.id}`
                : 'ico-sp-order',
            itemsList: itemsList,
            itemSelectedId: item.id,
            callback: () => {
                setInitialSocialProfilePosition(initialPosition);
                if (variant === 'social-profile' && globalInfo.group.activeGroup) {
                    dispatch({ type: CLEAR_GROUP_INFO });
                }
                setActiveItem(item);
                const activeElement = document.activeElement as HTMLButtonElement;
                activeElement.blur();
            }
        });
    };

    return (
        <div
            className="navigation-social-profile flex flex-col justify-center space-y-m"
            {...restProps}>
            <div role="list" className="flex flex-col space-y-2">
                {activeItem ? (
                    <motion.div
                        key={activeItem.id}
                        initial={{
                            translateY: `${initialSocialProfilePosition * 100}%`
                        }}
                        animate={{ translateY: 0 }}
                        transition={{
                            duration: SP_TRANSITION_DURATION_MS / 1000,
                            ease: [0.34, 1.36, 0.64, 1]
                        }}>
                        <AvatarMenu
                            data={activeItem}
                            selected
                            variant="social-profile"
                            data-testid="active-element"
                        />
                    </motion.div>
                ) : null}
                {orderedItems?.map((item, index) => {
                    if (index < (activeItem ? nbItemsDisplayed : nbItemsDisplayedNoActiveItem)) {
                        return (
                            <AvatarMenu
                                data-testid="visible-in-menu"
                                key={item.id}
                                variant="social-profile"
                                data={item}
                                onClick={() =>
                                    updateActive(item, index > 0 || activeItem ? index + 1 : 0)
                                }
                            />
                        );
                    }
                })}
                {nbMoreSocialProfiles > 0 ? (
                    <RightMenuPopover
                        activeItem={activeItem}
                        nbItems={nbMoreSocialProfiles}
                        itemsList={orderedItems}
                        updateActiveItem={(item: SocialProfileType) =>
                            updateActive(
                                item,
                                activeItem ? nbItemsDisplayed : nbItemsDisplayedNoActiveItem
                            )
                        }
                        variant="social-profile"
                    />
                ) : null}
            </div>
        </div>
    );
}

type SocialProfilesMenuProps = ComponentPropsWithoutRef<'div'> & {
    hasActiveItem?: boolean;
    initialSocialProfilePosition: number;
    setInitialSocialProfilePosition: (position: number) => void;
    variant?: 'social-profile';
};
export function SocialProfilesMenu({
    hasActiveItem = true,
    initialSocialProfilePosition,
    setInitialSocialProfilePosition,
    variant = 'social-profile',
    ...restProps
}: SocialProfilesMenuProps) {
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles();
    const { dispatch } = useGlobalInfo();

    return (
        <SocialProfilesMenuContent
            activeItem={hasActiveItem ? activeSocialProfile : null}
            itemsList={activatedSocialProfiles}
            setActiveItem={(item) => {
                dispatch({ type: UPDATE_ACTIVE_SOCIAL_PROFILE, payload: item });
            }}
            initialSocialProfilePosition={initialSocialProfilePosition}
            setInitialSocialProfilePosition={setInitialSocialProfilePosition}
            variant={variant}
            {...restProps}
        />
    );
}
